import { OnChanges, OnInit, SimpleChanges, Component, Input, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { CategoryInterface } from "@core/interfaces/forums/category.interface";
import { ForumInterface } from "@core/interfaces/forums/forum.interface";
import { TopicInterface } from "@core/interfaces/forums/topic.interface";
import { UserProfileInterface, PreferredForumFeedType } from "@core/interfaces/user-profile.interface";
import { ForumApiService } from "@core/services/api/forum/forum-api.service";
import { AuthService } from "@core/services/auth.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { DeviceService } from "@core/services/device.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { UpdateUserProfile } from "@features/account/store/auth.actions";
import { ForumTabType } from "@features/forum/forum-tab-type.enum";
import { ForumActionTypes, GetTopic, GetTopicSuccess, GetTopicFailure } from "@features/forum/store/forum.actions";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { map, switchMap, tap, takeUntil, take, filter } from "rxjs/operators";

@Component({
  selector: "astrobin-forum-preview",
  templateUrl: "./forum-preview.component.html",
  styleUrls: ["./forum-preview.component.scss"]
})
export class ForumPreviewComponent extends BaseComponentDirective implements OnInit, OnChanges {
  readonly MAX_TOPICS = 10;
  readonly ForumTabType = ForumTabType;

  @Input()
  forumId: ForumInterface["id"];

  @Input()
  showHeader = true;

  @Input()
  showFooter = true;

  @Input()
  striped = true;

  @Input()
  useCard = true;

  @Input()
  initialFeedType: PreferredForumFeedType;

  category: CategoryInterface;
  forum: ForumInterface;
  topics: TopicInterface[] = null;
  loading: boolean;
  enableNewForumExperience: boolean = false;
  isAuthenticated = false;
  loadingTopicId: number | null = null;
  currentUserProfile: UserProfileInterface | null = null;
  selectedFeedType: PreferredForumFeedType;

  forumUrl: string;
  forumNewTopicUrl: string;

  equipmentCategoryDescription: string;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly forumApiService: ForumApiService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly translateService: TranslateService,
    public readonly utilsService: UtilsService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly actions$: Actions,
    private readonly deviceService: DeviceService
  ) {
    super(store$);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.equipmentCategoryDescription = this.translateService.instant(
      "Every equipment item on AstroBin has a forum associated with it. Anybody who used an " +
        "equipment item is notified of new topics in its forum, unless they opt out. This is very  " +
        "useful to connect with other users who use the same equipment as you. " +
        "{{ _0 }}Learn more about the AstroBin equipment database!{{ _1 }}",
      {
        _0: `<a href="https://welcome.astrobin.com/features/equipment-database" target="_blank">`,
        _1: "</a>"
      }
    );

    // First get the profile to set enableNewForumExperience and currentUserProfile
    this.currentUserProfile$.pipe(take(1)).subscribe((profile: UserProfileInterface | null) => {
      this.currentUserProfile = profile;
      this.enableNewForumExperience =
        !profile || profile.enableNewForumExperience !== false || this.deviceService.isPwa();
      this._initializeFeedType();

      // Then load topics with the correct settings
      this._loadTopics();
    });

    // Continue subscribing for future changes
    this.currentUserProfile$.pipe(takeUntil(this.destroyed$)).subscribe((profile: UserProfileInterface | null) => {
      this.currentUserProfile = profile;
      this.enableNewForumExperience =
        !profile || profile.enableNewForumExperience !== false || this.deviceService.isPwa();

      // Don't re-initialize feed type when we're the ones who changed it
      // The profile update will reflect our change, so comparing preferredForumFeedType
      // with selectedFeedType would show they're equal after our own update

      // Update URLs if topics are already loaded
      if (this.topics && !this.loading) {
        this._updateUrls();
      }
    });

    this.authService
      .isAuthenticated$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.forumId) {
      this._loadTopics();
    }
  }

  topicUrl(topicId: TopicInterface["id"]): string {
    if (this.enableNewForumExperience) {
      return `/forum/topic/${topicId}`;
    }
    return `${this.classicRoutesService.FORUM_HOME}topic/${topicId}`;
  }

  onTopicClick(event: MouseEvent, topic: TopicInterface): void {
    // Only handle for new forum experience
    if (!this.enableNewForumExperience) {
      return;
    }

    // Only handle left clicks (button 0) without modifier keys
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) {
      return; // Let default behavior handle middle-click, ctrl+click, etc.
    }

    // Always prevent default navigation - we'll handle it ourselves
    event.preventDefault();
    event.stopPropagation();

    // Set loading state
    this.loadingTopicId = topic.id;
    this.changeDetectorRef.markForCheck();

    // Dispatch action to fetch full topic data
    this.store$.dispatch(new GetTopic({ topicId: topic.id }));

    // Also subscribe to failures to clear loading state
    this.actions$.pipe(ofType<GetTopicFailure>(ForumActionTypes.GET_TOPIC_FAILURE), take(1)).subscribe(() => {
      // Clear loading state on any topic failure
      this.loadingTopicId = null;
      this.changeDetectorRef.markForCheck();
    });

    // Subscribe to the topic being loaded
    this.actions$
      .pipe(
        ofType<GetTopicSuccess>(ForumActionTypes.GET_TOPIC_SUCCESS),
        filter(action => action.payload.topic.id === topic.id),
        take(1)
      )
      .subscribe(action => {
        const loadedTopic = action.payload.topic;

        // Build navigation parameters
        const queryParams: any = {};
        if (loadedTopic.targetPage && loadedTopic.targetPage > 1) {
          queryParams.page = loadedTopic.targetPage;
        }

        let fragment: string | null = null;
        if (this.isAuthenticated) {
          if (loadedTopic.firstUnreadPostId) {
            fragment = `post-${loadedTopic.firstUnreadPostId}`;
          } else if (loadedTopic.lastReadPostId) {
            fragment = `post-${loadedTopic.lastReadPostId}`;
          }
        }

        // Navigate with the correct page and fragment
        void this.router.navigate([`/forum/topic/${topic.id}`], {
          queryParams,
          fragment
        });

        // Clear loading state
        this.loadingTopicId = null;
        this.changeDetectorRef.markForCheck();
      });
  }

  protected topicTrackByFn(index: number, item: TopicInterface): number {
    return item.id;
  }

  public refresh(): void {
    this.topics = null;
    this.changeDetectorRef.markForCheck();

    // Add small delay to ensure UI updates before loading
    this.utilsService.delay(100).subscribe(() => {
      this.loading = false; // Reset loading so _loadTopics can run
      this._loadTopics();
    });
  }

  private _loadTopics(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;

    if (this.forumId) {
      this.forumApiService
        .getForum(this.forumId)
        .pipe(
          tap(forum => {
            this.forum = forum;
            this._updateUrls();
          }),
          switchMap(forum =>
            this.forumApiService.getCategory(forum.category).pipe(
              tap(category => (this.category = category)),
              map(() => forum)
            )
          ),
          switchMap(forum => this.forumApiService.loadTopics(forum.id))
        )
        .subscribe(response => {
          this.topics = response.results.slice(0, this.MAX_TOPICS);
          this.loading = false;
        });
    } else {
      this._updateUrls();

      // Only use selected feed type if new forum experience is enabled
      let topicsObservable;
      if (this.enableNewForumExperience && this.selectedFeedType) {
        switch (this.selectedFeedType) {
          case ForumTabType.MY_FEED:
            topicsObservable = this.forumApiService.getPersonalizedFeed(1);
            break;
          case ForumTabType.ACTIVE:
            topicsObservable = this.forumApiService.activeTopics(1);
            break;
          case ForumTabType.NEW:
            // For NEW, use loadTopics without forumId to get all new topics
            topicsObservable = this.forumApiService.loadTopics(undefined, 1);
            break;
          case ForumTabType.LATEST:
            topicsObservable = this.forumApiService.latestTopics();
            break;
          default:
            topicsObservable = this.forumApiService.latestTopics();
            break;
        }
      } else {
        // Classic experience or no selected feed type: always use latest
        topicsObservable = this.forumApiService.latestTopics();
      }

      topicsObservable.subscribe(response => {
        this.topics = response.results.slice(0, this.MAX_TOPICS);
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  private _updateUrls(): void {
    const useNewExperience = this.enableNewForumExperience || this.deviceService.isPwa();
    if (this.forumId && this.forum) {
      this.forumUrl = useNewExperience
        ? `/forum/c/equipment-forums/${this.forum.slug}`
        : `${this.classicRoutesService.FORUM_HOME}c/equipment-forums/${this.forum.slug}/`;
      this.forumNewTopicUrl = `${this.classicRoutesService.FORUM_HOME}forum/${this.forum.id}/topic/add/`;
    } else {
      this.forumUrl = useNewExperience ? `/forum` : `${this.classicRoutesService.FORUM_LATEST}`;
      this.forumNewTopicUrl = `${this.classicRoutesService.FORUM_HOME}/#new-topic`;
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Initialize the selected feed type based on user preference or defaults
   */
  private _initializeFeedType(): void {
    // If initialFeedType is provided from parent component, use it
    if (this.initialFeedType) {
      this.selectedFeedType = this.initialFeedType;
    } else {
      // Otherwise fall back to user preference or defaults
      if (this.isAuthenticated) {
        // Use user preference or default to MY_FEED for authenticated users
        this.selectedFeedType = this.currentUserProfile?.preferredForumFeedType || ForumTabType.MY_FEED;
      } else {
        // Default to ACTIVE for anonymous users
        this.selectedFeedType = ForumTabType.ACTIVE;
      }
    }
  }

  /**
   * Check if a feed type is valid for saving preferences
   */
  private _isValidFeedType(feedType: ForumTabType): boolean {
    return [ForumTabType.MY_FEED, ForumTabType.ACTIVE, ForumTabType.LATEST, ForumTabType.NEW].includes(feedType);
  }

  /**
   * Change the selected feed type and update user preferences
   */
  changeFeedType(feedType: PreferredForumFeedType): void {
    this.selectedFeedType = feedType;

    // Save preference if authenticated and this is a valid feed type
    if (this.isAuthenticated && this.currentUserProfile && this._isValidFeedType(feedType)) {
      this.store$.dispatch(
        new UpdateUserProfile({
          id: this.currentUserProfile.id,
          preferredForumFeedType: feedType
        })
      );
    }

    // Clear topics to show loading state
    this.topics = null;
    this.changeDetectorRef.markForCheck();

    // Reset loading flag before calling _loadTopics so it can actually run
    this.loading = false;

    // Reload topics with new feed type
    this._loadTopics();
  }

  /**
   * Get the icon for the current selected feed type
   */
  getCurrentFeedIcon(): IconProp {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return ["fas", "rss"];
      case ForumTabType.ACTIVE:
        return ["fas", "bolt"];
      case ForumTabType.NEW:
        return ["fas", "star"];
      case ForumTabType.LATEST:
        return ["fas", "clock"];
      default:
        return ["fas", "rss"]; // Default to my feed
    }
  }

  /**
   * Get the label for the current selected feed type
   */
  getCurrentFeedLabel(): string {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return this.translateService.instant("My forum feed");
      case ForumTabType.ACTIVE:
        return this.translateService.instant("Active forum topics");
      case ForumTabType.NEW:
        return this.translateService.instant("New forum topics");
      case ForumTabType.LATEST:
        return this.translateService.instant("Latest from the forums");
      default:
        return this.translateService.instant("My forum feed"); // Default to my forum feed
    }
  }

  /**
   * Get the subtitle for the current selected feed type
   */
  getCurrentFeedSubtitle(): string {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return this.translateService.instant("A curated list of topics that you can customize.");
      case ForumTabType.ACTIVE:
        return this.translateService.instant("Topics with a high activity at the moment.");
      case ForumTabType.NEW:
        return this.translateService.instant("Topics that were recently started.");
      case ForumTabType.LATEST:
        return this.translateService.instant("Topics with recent replies.");
      default:
        return this.translateService.instant("Recent forum posts");
    }
  }

  /**
   * Get only accepted tags
   */
  getAcceptedTags(tags: TopicInterface["tags"]): TopicInterface["tags"] {
    if (!tags) {
      return [];
    }
    return tags.filter(tag => tag.status === "accepted");
  }
}
